import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttachmentResponse, AttachmentTarget } from '@orion/contracts';
import { api, ApiError, getCsrfToken } from '../../lib/api-client.js';

function queryString(target: AttachmentTarget): string {
  return `?${new URLSearchParams(target as Record<string, string>).toString()}`;
}

export function useAttachmentsQuery(target: AttachmentTarget) {
  return useQuery({
    queryKey: ['attachments', target],
    queryFn: () => api.get<AttachmentResponse[]>(`/attachments${queryString(target)}`),
  });
}

export function useUploadAttachment(target: AttachmentTarget) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const csrf = getCsrfToken();
      const res = await fetch(`/api/v1/attachments${queryString(target)}`, {
        method: 'POST',
        credentials: 'include',
        // No Content-Type here — the browser sets the multipart boundary itself.
        headers: csrf ? { 'X-CSRF-Token': csrf } : undefined,
        body: form,
      });
      const payload = await res.json();
      if (!res.ok) throw new ApiError(payload, res.status);
      return payload.data as AttachmentResponse;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', target] }),
  });
}

export function useDeleteAttachment(target: AttachmentTarget) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/attachments/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['attachments', target] }),
  });
}
